import React, { useState, useEffect, useRef } from "react";
import styles from "../css/css_pages/newIssue.module.css";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import config from "../../config";
import PopupMessage from "../layouts/PopupMessage";
import { useTranslation } from 'react-i18next';

const TicketProduct = {
  AI_BANKER: "ai-banker",
  COOP_MIS: "coop-mis",
  POS: "pos"
};

const TicketType = {
  NOT_WORKING: "not-working",
  DATA_CORRECTION: "data-correction"
};

const TicketPriority = {
  NORMAL: "normal",
  MEDIUM: "medium",
  HIGH: "high"
};

const CreateIssue = () => {
  const { t } = useTranslation();
  const [token, setToken] = useState();
  const [description, setDescription] = useState("");
  const [date, setDate] = useState("");
  const [priority, setPriority] = useState("");
  const [issueType, setIssueType] = useState("");
  const [product, setProduct] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [issuePhone, setIssuePhone] = useState("");
  const [useSavedPhone, setUseSavedPhone] = useState(false);
  const [clientMobile, setClientMobile] = useState("");
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const productOptions = Object.values(TicketProduct);
  const typeOptions = Object.values(TicketType);
  const priorityOptions = Object.values(TicketPriority);

  useEffect(() => {
    const authToken = Cookies.get("authToken");
    setToken(authToken);
    const currentDate = new Date().toISOString().slice(0, 10);
    setDate(currentDate);

    const fetchClientMobile = async () => {
      try {
        const response = await fetch(`${config.API_URL}/client/get-client-details`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const client = await response.json();
        console.log("Client details", client)
        const client_details = client.response
        if (client_details.mobile) {
          setClientMobile(client_details.mobile || "");
        }
      } catch (error) {
        console.error("Error fetching client mobile:", error);
        showPopupMessage(t('client_fetch_error'));
      }
    };

    if (authToken) {
      fetchClientMobile();
    }
  }, [t]);

  const showPopupMessage = (message) => {
    setAlertMessage(message);
  };

  const closePopupMessage = () => {
    setAlertMessage(null);
  };

  const handleFileChange = (e) => {
    try {
      const files = Array.from(e.target.files);
      const validFiles = files.filter(file => {
        if (file.size > 5 * 1024 * 1024) {
          showPopupMessage(`${t('file_too_large')} ${file.name}`);
          return false;
        }
        return true;
      });
      setUploadedFiles((prevFiles) => [...prevFiles, ...validFiles]);
    } catch (error) {
      console.error("Error handling file change:", error);
      showPopupMessage(t('file_processing_error'));
    }
  };

  const handleFileClick = (file) => {
    try {
      if (selectedFile) {
        URL.revokeObjectURL(URL.createObjectURL(selectedFile));
      }
      setSelectedFile(file);
      setShowModal(true);
    } catch (error) {
      console.error("Error handling file click:", error);
      showPopupMessage(t('file_preview_error'));
    }
  };

  const handleRemoveFile = (fileToRemove) => {
    try {
      setUploadedFiles((prevFiles) =>
        prevFiles.filter((file) => file !== fileToRemove)
      );
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error("Error removing file:", error);
      showPopupMessage(t('file_remove_error'));
    }
  };

  const handleModalClose = () => {
    try {
      if (selectedFile) {
        URL.revokeObjectURL(URL.createObjectURL(selectedFile));
      }
      setShowModal(false);
      setSelectedFile(null);
    } catch (error) {
      console.error("Error closing modal:", error);
    }
  };

  const handleUseSavedPhoneChange = (e) => {
    const isChecked = e.target.checked;
    setUseSavedPhone(isChecked);
    if (isChecked && clientMobile) {
      setIssuePhone(clientMobile);
    } else {
      setIssuePhone("");
    }
  };

  const handleIssuePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 10) {
      setIssuePhone(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    const validationErrors = [];
    if (!description.trim()) validationErrors.push(t('description_required'));
    if (!issueType) validationErrors.push(t('issue_type_required'));
    if (!priority) validationErrors.push(t('priority_required'));
    if (!product) validationErrors.push(t('product_required'));
    if (issuePhone && issuePhone.length !== 10) validationErrors.push(t('phone_invalid'));

    if (validationErrors.length > 0) {
      showPopupMessage(validationErrors.join("\n"));
      return;
    }

    setIsSubmitting(true);

    try {
      if (!token) {
        throw new Error(t('auth_error'));
      }

      const formData = new FormData();
      formData.append("issue", description.trim());
      formData.append("ticket_type", issueType);
      formData.append("ticket_priority", priority);
      formData.append("product", product);
      
      // Only append phone if it's provided
      const contactPhone = useSavedPhone ? clientMobile : issuePhone;
      if (contactPhone) {
        formData.append("contact_phone", contactPhone);
      }

      if (uploadedFiles.length > 0) {
        uploadedFiles.forEach((file) => {
          formData.append("files", file);
        });
      }

      const response = await fetch(`${config.API_URL}/client/create-ticket`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || t('create_error'));
      }

      showPopupMessage(t('ticket_created'));

      setTimeout(() => {
        navigate("/");
        setDescription("");
        setPriority("");
        setIssueType("");
        setProduct("");
        setUploadedFiles([]);
        setIssuePhone("");
        setUseSavedPhone(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
        setIsSubmitting(false);
      }, 2000);

    } catch (error) {
      console.error("Error creating issue:", error);
      showPopupMessage(error.message || t('network_error'));
      setIsSubmitting(false);
    }
  };

  const translateProductName = (productValue) => {
    switch (productValue) {
      case TicketProduct.AI_BANKER:
        return t('ai_banker');
      case TicketProduct.COOP_MIS:
        return t('coop_mis');
      case TicketProduct.POS:
        return t('pos');
      default:
        return productValue;
    }
  };

  const translateTypeName = (typeValue) => {
    switch (typeValue) {
      case TicketType.NOT_WORKING:
        return t('not_working');
      case TicketType.DATA_CORRECTION:
        return t('data_correction');
      default:
        return typeValue;
    }
  };

  return (
    <div className={styles.formContainer}>
      {alertMessage && (
        <PopupMessage message={alertMessage} onClose={closePopupMessage} />
      )}
      <h1 className={styles.formTitle}>{t('new_issue_form')}</h1>
      <div className={styles.formWrapper}>
        <div className={styles.leftSection}>
          <div className={styles.card}>
            <h3>{t('description')}</h3>
            <div className={styles.formGroup}>
              <label>
                {t('description')} <span className={styles.required}>*</span>
              </label>
              <textarea
                placeholder={t('describe_issue')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                className={styles.formGroupTextarea}
              />
            </div>

            <div className={styles.formGroup}>
              <label>
                {t('product')} <span className={styles.required}>*</span>
              </label>
              <div className={styles.productRadioGrid}>
                {productOptions.map((productOption) => (
                  <label key={productOption} className={styles.productRadioOption}>
                    <input
                      type="radio"
                      name="product"
                      value={productOption}
                      checked={product === productOption}
                      onChange={() => setProduct(productOption)}
                    />
                    <span className={styles.radioLabel}>
                      {translateProductName(productOption)}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className={styles.checkboxContainer}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={useSavedPhone}
                  onChange={handleUseSavedPhoneChange}
                  disabled={!clientMobile}
                />
                <span>{t('use_saved_phone')}</span>
              </label>
            </div>
            
            <div className={styles.formGroup}>
              <label>{t('contact_phone')}</label>
              <input
                type="tel"
                value={issuePhone}
                onChange={handleIssuePhoneChange}
                maxLength="10"
                disabled={useSavedPhone}
                placeholder={t('phone_placeholder')}
                className={styles.formGroupInput}
              />
            </div>

            <div className={styles.fileUploadSection}>
              <div className={styles.formGroup}>
                <label>{t('attach_resources')}</label>
                <input
                  type="file"
                  onChange={handleFileChange}
                  multiple
                  accept=".jpg,.jpeg,.png,.pdf,.zip,.txt"
                  ref={fileInputRef}
                  className={styles.fileInput}
                />
              </div>
              <ul className={styles.uploadedFilesList}>
                {uploadedFiles.map((file, index) => (
                  <li key={index}>
                    <span onClick={() => handleFileClick(file)}>
                      {file.name} ({(file.size / 1024).toFixed(2)} KB)
                    </span>
                    <span
                      className={styles.removeFileBtn}
                      onClick={() => handleRemoveFile(file)}
                    >
                      &#10005;
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className={styles.rightSection}>
          <div className={styles.card}>
            <h3>{t('information')}</h3>
            <form onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label>
                  {t('date')} <span className={styles.required}>*</span>
                </label>
                <input
                  type="date"
                  value={date}
                  readOnly
                  className={styles.formGroupInput}
                />
              </div>
              <div className={styles.formGroup}>
                <label>
                  {t('type')} <span className={styles.required}>*</span>
                </label>
                <select
                  value={issueType}
                  onChange={(e) => setIssueType(e.target.value)}
                  required
                  className={styles.formGroupSelect}
                >
                  <option value="">{t('select_issue_type')}</option>
                  {typeOptions.map((type) => (
                    <option key={type} value={type}>
                      {translateTypeName(type)}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label>
                  {t('priority')} <span className={styles.required}>*</span>
                </label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  required
                  className={styles.formGroupSelect}
                >
                  <option value="">{t('select_priority')}</option>
                  {priorityOptions.map((priorityOption) => (
                    <option key={priorityOption} value={priorityOption}>
                      {t(priorityOption)}
                    </option>
                  ))}
                </select>
              </div>
              <button
                className={styles.submitButton}
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? t('sending') : t('send')}
              </button>
            </form>
          </div>
        </div>
      </div>

      {showModal && selectedFile && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <span className={styles.close} onClick={handleModalClose}>
              &times;
            </span>
            <h3>{t('file_details')}</h3>
            <p>{t('name')}: {selectedFile.name}</p>
            <p>{t('type_label')}: {selectedFile.type}</p>
            <p>{t('size')}: {(selectedFile.size / 1024).toFixed(2)} KB</p>

            {selectedFile.type.startsWith("image/") ? (
              <img
                src={URL.createObjectURL(selectedFile)}
                alt={selectedFile.name}
                className={styles.modalImage}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '';
                  e.target.alt = t('image_load_error');
                }}
              />
            ) : (
              <div className={styles.filePreviewPlaceholder}>
                <p>{t('no_preview_available')}</p>
                <a
                  href={URL.createObjectURL(selectedFile)}
                  download={selectedFile.name}
                  onClick={(e) => {
                    setTimeout(() => {
                      URL.revokeObjectURL(e.target.href);
                    }, 100);
                  }}
                >
                  {t('download_file')}
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateIssue;